import { OrganizationAdministration, OrganizationQuestionnaire } from "../../../api";
import { useAuth } from "../../../context";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { showErrorToast, showSuccessToast } from "../../../utils";
import { useToast } from "@chakra-ui/react";
import { t } from "i18next";
import { hideAppSpinner, showAppSpinner } from "../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";

export const organizationQuestionnaireKeys = {
  allDataEntryPlanningQuestionnaires: () => ["allDataEntryPlanningQuestionnaires"],
  dataEntryPlanningQuestionnaireById: () => ["dataEntryPlanningQuestionnaireById"],
  allOrgQuestionnairesByUser: () => ["allOrgQuestionnairesByUser"],
  orgQuestionnaireById: () => [...organizationQuestionnaireKeys.allOrgQuestionnairesByUser(), "orgQuestionnaireById"],
  items: () => [...organizationQuestionnaireKeys.allOrgQuestionnairesByUser(), "orgQuestionnaireById"],
  sourceDataTasks: () => ["sourceDataTasks"],
};

export function useGetAllDataEntryPlanningQuestionnaires(queryParams, config) {
  const { callApi } = useAuth();
  return useQuery(
    [...organizationQuestionnaireKeys.allDataEntryPlanningQuestionnaires(), queryParams],
    () => callApi(OrganizationQuestionnaire.getAllQuestionnaires, queryParams),
    config
  );
}

export function useGetQuestionnaireById(id, config) {
  const { callApi } = useAuth();
  return useQuery(
    [...organizationQuestionnaireKeys.dataEntryPlanningQuestionnaireById(), id],
    () => callApi(OrganizationQuestionnaire.getQuestionnaireById, id),
    config
  );
}

// Insights "Source Data" cards — FINALIZED tasks with entered data for the given scope + period +
// dimension filter. `params` = { scope, year, quarter, dimensionItemIds } (dimensionItemIds is the
// pre-serialized "dimensionOne=1,2&..." fragment). scope/year/quarter/dimension all key the query.
export function useGetSourceDataTasks(params, config) {
  const { callApi } = useAuth();
  return useQuery(
    [...organizationQuestionnaireKeys.sourceDataTasks(), params.scope, params.year, params.quarter, params.dimensionItemIds],
    () => callApi(OrganizationQuestionnaire.getSourceDataTasks, params),
    config
  );
}

export function useCreateQuestionnaire(config) {
  const { callApi } = useAuth();
  return useMutation((data) => callApi(OrganizationQuestionnaire.createQuestionnaire, data), config);
}

export function useBulkCreateQuestionnaires(config) {
  const { callApi } = useAuth();
  return useMutation((data) => callApi(OrganizationQuestionnaire.bulkCreateQuestionnaires, data), config);
}

export function useUpdateQuestionnaire(config) {
  const { callApi } = useAuth();
  return useMutation(({ id, data }) => callApi(OrganizationQuestionnaire.updateQuestionnaire, { id, data }), config);
}

export function useDeleteQuestionnaire(config) {
  const { callApi } = useAuth();
  return useMutation((id) => callApi(OrganizationQuestionnaire.deleteQuestionnaire, id), config);
}

// Manual "Send reminder" action (task card kebab menu). Refetches the planning list on success, which
// is where each card's "Reminder sent …" line comes from (`lastReminder`).
export function useSendTaskReminder(config) {
  const { callApi } = useAuth();
  const queryClient = useQueryClient();
  return useMutation(({ id, data }) => callApi(OrganizationQuestionnaire.sendTaskReminder, { id, data }), {
    ...config,
    onSuccess: (...args) => {
      queryClient.invalidateQueries(organizationQuestionnaireKeys.allDataEntryPlanningQuestionnaires());
      config?.onSuccess?.(...args);
    },
  });
}

export function useDefaultOrganizationQuestionnaireConfig() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const toast = useToast();

  return {
    onMutate: () => dispatch(showAppSpinner()),
    onSuccess: () => {
      showSuccessToast(toast, t("organizationQuestionnaire.page.finalized"));
      queryClient.invalidateQueries(organizationQuestionnaireKeys.orgQuestionnaireById());
    },
    onError: (err) => showErrorToast(toast, err.message),
    onSettled: () => dispatch(hideAppSpinner()),
  };
}

export function useFinalizeQuestionnaire(customConfig) {
  let config = useDefaultOrganizationQuestionnaireConfig();
  if (customConfig) {
    config = customConfig;
  }
  const { callApi } = useAuth();
  return useMutation((id) => callApi(OrganizationQuestionnaire.finalizeQuestionnaire, id), config);
}

export function useGetOrganizationStructure(config) {
  const { callApi } = useAuth();

  return useQuery(["orgStructure"], () => callApi(OrganizationAdministration.getOrganizationStructure), config);
}

export function useImportScopeOneAndTwoData(config) {
  const { callApi } = useAuth();
  return useMutation(
    ({ questionnaireId, data, groupType }) =>
      callApi(OrganizationQuestionnaire.importScopeOneAndTwoData, { questionnaireId, data, groupType }),
    config
  );
}

export function useGetOrganisationQuestionnaires(params, config) {
  const { callApi } = useAuth();
  return useQuery(
    [...organizationQuestionnaireKeys.allOrgQuestionnairesByUser(), params.page, params.size],
    () => callApi(OrganizationQuestionnaire.getOrganisationQuestionnaires, params),
    config
  );
}

export function useGetQuestionnaireDataById(id, config) {
  const { callApi } = useAuth();
  return useQuery(
    [...organizationQuestionnaireKeys.orgQuestionnaireById(), id],
    () => callApi(OrganizationQuestionnaire.getQuestionnaireDataById, id),
    config
  );
}

export function useUnfinaliseOrgQuestionnaire(config) {
  const { callApi } = useAuth();
  return useMutation(
    (questionnaireId) => callApi(OrganizationQuestionnaire.unfinaliseOrgQuestionnaire, questionnaireId),
    config
  );
}

export function useUpdateMethodology(config) {
  const { callApi } = useAuth();
  return useMutation(
    ({ questionnaireId, data }) =>
      callApi(OrganizationQuestionnaire.updateMethodology, { questionnaireId, data }),
    config
  );
}

export function useUpdateQuestionnaireFiles(config) {
  const { callApi } = useAuth();
  return useMutation(
    ({ questionnaireId, data }) =>
      callApi(OrganizationQuestionnaire.updateQuestionnaireFiles, { questionnaireId, data }),
    config
  );
}

export function useDownloadScopeOneTemplate(config) {
  const { callApi } = useAuth();
  return useMutation(
    (groupType) => callApi(OrganizationQuestionnaire.downloadScopeOneTemplate, { groupType }),
    config
  );
}

export function useDownloadScopeTwoTemplate(config) {
  const { callApi } = useAuth();
  return useMutation(() => callApi(OrganizationQuestionnaire.downloadScopeTwoTemplate), config);
}
